import { AccessToken } from '@okta/okta-auth-js';
import { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import useUserInfo from './useUserInfo';
import { HeaderData } from '../adapters/types';
import { getDefaultStore, getDepartmentByStore } from '../adapters/utils';

const useAuthHeaders = (): {
  headers?: HeaderData;
  isReady: boolean;
} => {
  const { accessToken, userInfo } = useUserInfo();
  const [remoteUser, setRemoteUser] = useState<string>('');
  const [enterpriseId, setEnterpriseId] = useState<string>('');
  const [storeId, setStoreId] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [token, setToken] = useState<AccessToken>();
  const [headers, setHeaders] = useState<HeaderData>();
  const [isReady, setReady] = useState(false);

  // Effect for setting the token
  useEffect(() => {
    if (accessToken) {
      setToken(accessToken);
    }
  }, [accessToken]);

  // Effect for setting user info related states
  useEffect(() => {
    if (userInfo) {
      const enterpriseIdFromUser = get(userInfo, 'iamUser.enterprise.id', '') as string;
      setEnterpriseId(enterpriseIdFromUser);

      const defaultStoreId = getDefaultStore(userInfo);
      setStoreId(defaultStoreId);

      const departmentName = getDepartmentByStore(userInfo, defaultStoreId);
      setDepartment(departmentName);
    }
  }, [userInfo]);

  // Effect for setting remoteUser
  useEffect(() => {
    if (userInfo) {
      setRemoteUser(get(userInfo, 'iamUser.username', ''));
    }
  }, [userInfo]);

  // Memo for setting headers
  useMemo(() => {
    if (remoteUser && enterpriseId && storeId && department && token) {
      const authorization = `${token.tokenType} ${token.accessToken}`;
      const headerObject: HeaderData = {
        'x-department-name': department,
        remote_user: remoteUser,
        'x-enterprise-id': enterpriseId,
        'x-store-id': storeId,
        authorization,
        'x-department-type': 'A',
      };

      setHeaders(headerObject);
      setReady(true);
    }
  }, [remoteUser, enterpriseId, storeId, department, token]);

  return { headers, isReady };
};

export default useAuthHeaders;
